import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { auth } from '../firebaseConfig';
import { onAuthStateChanged, onIdTokenChanged } from 'firebase/auth';
import AuthLoadingScreen from '../components/AuthLoadingScreen';
import CrashlyticsService from '../services/CrashlyticsService.js';
import {
  startExpirationMonitoring,
  stopExpirationMonitoring,
  clearProactiveRefresh,
  refreshTokenWithRetry
} from '../utils/tokenRefreshUtils';

// Import ErrorContext dynamically to handle token refresh errors (Phase 4)
let errorContextHandler = null;

/**
 * Set error handler for token refresh errors (called from ErrorProvider)
 * This allows AuthContext to show user-friendly error messages
 */
export const setTokenRefreshErrorHandler = (handler) => {
  errorContextHandler = handler;
};

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Token refresh state management
  const [currentToken, setCurrentToken] = useState(null);
  const [tokenRefreshing, setTokenRefreshing] = useState(false);
  const [tokenExpirationTime, setTokenExpirationTime] = useState(null);
  const [lastTokenRefreshError, setLastTokenRefreshError] = useState(null);
  
  // Track if token refresh is in progress to prevent multiple simultaneous refreshes
  const tokenRefreshInProgress = useRef(false);
  const tokenRefreshListeners = useRef(new Set());

  // Function to get current token with automatic refresh if needed
  const getIdToken = useCallback(async (forceRefresh = false) => {
    if (!currentUser) {
      return null;
    }

    try {
      const token = await currentUser.getIdToken(forceRefresh);
      
      // Decode token to get expiration time (payload is base64 encoded)
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expirationTime = payload.exp * 1000; // Convert to milliseconds
        setTokenExpirationTime(expirationTime);
      } catch (decodeError) {
        // Token expiration decode error - handled silently
      }
      
      setCurrentToken(token);
      setLastTokenRefreshError(null);
      return token;
    } catch (error) {
      setLastTokenRefreshError(error);
      
      // Log token refresh error in Crashlytics
      try {
        const crashlyticsService = CrashlyticsService.getInstance();
        
        // Add context about token refresh
        crashlyticsService.setCustomKeys([
          { key: 'token_refresh_attempt', value: 'initial' },
          { key: 'token_expiration_time', value: tokenExpirationTime ? new Date(tokenExpirationTime).toISOString() : 'unknown' },
          { key: 'user_id', value: currentUser?.uid || 'unknown' }
        ]);
        
        crashlyticsService.recordError(error, 'Token refresh error');
      } catch (crashlyticsError) {
        // Crashlytics logging error - handled silently
      }
      
      throw error;
    }
  }, [currentUser]);

  // Register token refresh listener
  const onTokenRefresh = useCallback((callback) => {
    tokenRefreshListeners.current.add(callback);
    return () => {
      tokenRefreshListeners.current.delete(callback);
    };
  }, []);

  // Listen for auth state changes (login/logout)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      // Reset token state when user changes
      if (!user) {
        setCurrentToken(null);
        setTokenExpirationTime(null);
        setTokenRefreshing(false);
        setLastTokenRefreshError(null);
        tokenRefreshInProgress.current = false;
        stopExpirationMonitoring();
        clearProactiveRefresh();
      } else {
        // Get initial token when user logs in
        try {
          setTokenRefreshing(true);
          await getIdToken(false);
        } catch (error) {
          // Error getting initial token - handled silently
        } finally {
          setTokenRefreshing(false);
        }
      }
      
      // Set user information in Crashlytics
      try {
        const crashlyticsService = CrashlyticsService.getInstance();
        if (user) {
          await crashlyticsService.setUser({
            id: user.uid,
            email: user.email || undefined,
            name: user.displayName || undefined
          });
          await crashlyticsService.log(`User logged in: ${user.email || user.uid}`);
        } else {
          await crashlyticsService.log('User logged out');
        }
      } catch (error) {
        // Failed to set user in Crashlytics - handled silently
      }
      
      setLoading(false);
      setInitialized(true);
    }, (error) => {
      // Log auth error in Crashlytics
      try {
        const crashlyticsService = CrashlyticsService.getInstance();
        
        // Add context about auth state change
        crashlyticsService.setCustomKeys([
          { key: 'auth_state_change', value: 'error' },
          { key: 'previous_user_id', value: currentUser?.uid || 'none' },
          { key: 'error_code', value: error.code || 'unknown' }
        ]);
        
        crashlyticsService.recordError(error, 'Auth state change');
      } catch (crashlyticsError) {
        // Crashlytics logging error - handled silently
      }
      
      setLoading(false);
      setInitialized(true);
    });

    return () => {
      unsubscribe();
    };
  }, [getIdToken]);

  // Proactive token refresh handler (Phase 2: Proactive Token Refresh)
  const handleProactiveRefresh = useCallback(async () => {
    if (!currentUser || tokenRefreshInProgress.current) {
      return;
    }

    try {
      tokenRefreshInProgress.current = true;
      setTokenRefreshing(true);

      // Use retry logic for proactive refresh
      const newToken = await refreshTokenWithRetry(async () => {
        return await getIdToken(true); // Force refresh
      });

      // Notify all registered listeners about token refresh
      tokenRefreshListeners.current.forEach(callback => {
        try {
          callback(newToken);
        } catch (listenerError) {
          // Error in token refresh listener - handled silently
        }
      });
      } catch (error) {
        setLastTokenRefreshError(error);
        
        // Handle token refresh error with user feedback (Phase 4)
        if (errorContextHandler) {
          try {
            errorContextHandler(error, {
              context: 'Proactive token refresh',
              silent: false // Show user-friendly error message
            });
          } catch (handlerError) {
            // Error in token refresh error handler - handled silently
          }
        }
        
        // Log token refresh error in Crashlytics
        try {
          const crashlyticsService = CrashlyticsService.getInstance();
          
          // Add context about proactive token refresh
          crashlyticsService.setCustomKeys([
            { key: 'token_refresh_type', value: 'proactive' },
            { key: 'token_expiration_time', value: tokenExpirationTime ? new Date(tokenExpirationTime).toISOString() : 'unknown' },
            { key: 'user_id', value: currentUser?.uid || 'unknown' }
          ]);
          
          crashlyticsService.recordError(error, 'Proactive token refresh failed');
        } catch (crashlyticsError) {
          // Crashlytics logging error - handled silently
        }
      } finally {
        setTokenRefreshing(false);
        tokenRefreshInProgress.current = false;
      }
    }, [currentUser, getIdToken]);

  // Start/stop proactive token refresh monitoring (Phase 2: Proactive Token Refresh)
  useEffect(() => {
    if (!currentUser || !tokenExpirationTime) {
      stopExpirationMonitoring();
      return;
    }

    // Start monitoring token expiration and schedule proactive refresh
    startExpirationMonitoring(handleProactiveRefresh, tokenExpirationTime);

    return () => {
      stopExpirationMonitoring();
    };
  }, [currentUser, tokenExpirationTime, handleProactiveRefresh]);

  // Listen for token refresh events (Phase 1: Token Refresh Listener)
  useEffect(() => {
    if (!currentUser) {
      return;
    }

    const unsubscribeToken = onIdTokenChanged(auth, async (user) => {
      if (!user) {
        setCurrentToken(null);
        setTokenExpirationTime(null);
        stopExpirationMonitoring();
        return;
      }

      // Prevent multiple simultaneous token refresh operations
      if (tokenRefreshInProgress.current) {
        return;
      }

      try {
        tokenRefreshInProgress.current = true;
        setTokenRefreshing(true);

        // Get the new token
        const newToken = await getIdToken(false);

        // Notify all registered listeners about token refresh
        tokenRefreshListeners.current.forEach(callback => {
          try {
            callback(newToken);
          } catch (listenerError) {
            // Error in token refresh listener - handled silently
          }
        });
      } catch (error) {
        setLastTokenRefreshError(error);
        
        // Handle token refresh error with user feedback (Phase 4)
        if (errorContextHandler) {
          try {
            errorContextHandler(error, {
              context: 'Token refresh',
              silent: false // Show user-friendly error message
            });
          } catch (handlerError) {
            // Error in token refresh error handler - handled silently
          }
        }
        
        // Log token refresh error in Crashlytics
        try {
          const crashlyticsService = CrashlyticsService.getInstance();
          
          // Add context about token refresh failure
          crashlyticsService.setCustomKeys([
            { key: 'token_refresh_type', value: 'automatic' },
            { key: 'token_expiration_time', value: tokenExpirationTime ? new Date(tokenExpirationTime).toISOString() : 'unknown' },
            { key: 'user_id', value: currentUser?.uid || 'unknown' }
          ]);
          
          crashlyticsService.recordError(error, 'Token refresh failed');
        } catch (crashlyticsError) {
          // Crashlytics logging error - handled silently
        }
      } finally {
        setTokenRefreshing(false);
        tokenRefreshInProgress.current = false;
      }
    });

    return () => {
      unsubscribeToken();
      stopExpirationMonitoring();
    };
  }, [currentUser, getIdToken]);

  const value = {
    currentUser,
    loading,
    // Token management
    currentToken,
    tokenRefreshing,
    tokenExpirationTime,
    lastTokenRefreshError,
    getIdToken,
    onTokenRefresh
  };

  return (
    <AuthContext.Provider value={value}>
      {(!loading && initialized) ? children : (
        <AuthLoadingScreen />
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 